// The public booking flow page, parameterized by host slug (ADR-0002 section 5).
// Orchestrates the three steps against the per-host public routes:
//   1) choose a booking type  -> GET /api/hosts/:slug/booking-types
//   2) pick a date + time      -> GET /api/hosts/:slug/availability
//   3) enter details + confirm -> POST /api/hosts/:slug/bookings -> confirmation
// Owns data fetching, timezone state, and error handling for each step. A 404 on
// the first load (unknown slug) is surfaced as a dedicated "host not found"
// state rather than a generic error.
// Implements: ADR-0002 section 5; architecture.md v1.0 section 5.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { ApiError } from '../api/types.js';
import type { BookingType, PublicBooking, Slot } from '../api/types.js';
import { api } from '../api/client.js';
import { navigate } from '../lib/router.js';
import { detectTimeZone, rollingWindow } from '../lib/time.js';
import { BrandShell } from '../components/BrandShell.js';
import { Stepper } from '../components/Stepper.js';
import { BookingTypeList } from '../components/BookingTypeList.js';
import { SlotPicker } from '../components/SlotPicker.js';
import { DetailsForm } from '../components/DetailsForm.js';
import { Confirmation } from '../components/Confirmation.js';
import { Banner, CardSkeletons, StateBlock } from '../components/feedback.js';

type Step = 'types' | 'slots' | 'details' | 'done';
type Load = 'loading' | 'ready' | 'error';
// A dedicated load outcome for the first fetch: 'notfound' means the slug does
// not resolve to a host (the public route 404s), shown as its own state.
type TypesLoad = Load | 'notfound';

// How far ahead to request availability. The engine clamps to the host's
// configured rolling window; this is a generous upper bound for the query.
const WINDOW_DAYS = 30;

export function BookingPage({
  slug,
  eventSlug,
}: {
  slug: string;
  /**
   * When present, this is a PRIVATE-event page (`/{slug}/{eventSlug}`, ADR-0003
   * section 3): the page fetches that one event (listed or unlisted) and drives
   * the existing slot-picker/booking flow for it directly, skipping the landing
   * type list. Absent means the normal landing booking page for the host.
   */
  eventSlug?: string;
}): JSX.Element {
  // Single-event mode is fixed for the life of the page (the route determines it).
  const singleEvent = eventSlug !== undefined;
  const [step, setStep] = useState<Step>('types');
  const [tz, setTz] = useState<string>(() => detectTimeZone());
  // Availability is timezone-independent (UTC instants), so the single-event
  // initial load reads the current zone via a ref rather than re-fetching the
  // event whenever the visitor changes their timezone.
  const tzRef = useRef(tz);
  tzRef.current = tz;

  // The host's public name, fetched alongside the booking types so the page can
  // say whose calendar is being booked. Best-effort: if it has not arrived (or
  // the profile fetch fails) the header falls back to the Numaco brand copy. The
  // unknown-slug case is owned by the booking-types load below (its 404 drives
  // the dedicated not-found state), so this fetch never needs its own 404 path.
  const [hostName, setHostName] = useState<string>();

  // Step 1: booking types
  const [types, setTypes] = useState<BookingType[]>([]);
  const [typesLoad, setTypesLoad] = useState<TypesLoad>('loading');
  const [typesError, setTypesError] = useState<string>();

  // Step 2: availability
  const [selectedType, setSelectedType] = useState<BookingType | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoad, setSlotsLoad] = useState<Load>('loading');
  const [slotsError, setSlotsError] = useState<string>();

  // Step 3: confirmation
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string>();
  const [booking, setBooking] = useState<PublicBooking | null>(null);
  const [manageToken, setManageToken] = useState<string>('');

  // Load booking types on mount (and when the slug changes). A 404 means the
  // slug does not resolve to a host: surface it as a distinct not-found state.
  const loadTypes = useCallback(async () => {
    setTypesLoad('loading');
    setTypesError(undefined);
    try {
      const res = await api.listBookingTypes(slug);
      setTypes(res.bookingTypes);
      setTypesLoad('ready');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setTypesLoad('notfound');
        return;
      }
      setTypesError(messageFor(err, 'We could not load booking options.'));
      setTypesLoad('error');
    }
  }, [slug]);

  // Load the host's public name (slug + displayName only) in parallel with the
  // types. Failures are swallowed: the header simply keeps the brand fallback,
  // and the not-found state is still driven by the booking-types 404 above.
  const loadHost = useCallback(async () => {
    setHostName(undefined);
    try {
      const res = await api.getPublicHost(slug);
      setHostName(res.displayName);
    } catch {
      setHostName(undefined);
    }
  }, [slug]);

  useEffect(() => {
    void loadHost();
  }, [loadHost]);

  // Load availability for the chosen type. tz is sent for display echo only.
  const loadSlots = useCallback(
    async (type: BookingType, zone: string) => {
      setSlotsLoad('loading');
      setSlotsError(undefined);
      const { from, to } = rollingWindow(WINDOW_DAYS);
      try {
        const res = await api.getAvailability(slug, { type: type.id, from, to, tz: zone });
        setSlots(res.slots);
        setSlotsLoad('ready');
      } catch (err) {
        setSlotsError(messageFor(err, 'We could not load availability.'));
        setSlotsLoad('error');
      }
    },
    [slug],
  );

  // Private-event mode: fetch the single event by its slug, then drop straight
  // into the slot picker for it (skipping the landing type list). A 404 (unknown
  // event or host) reuses the same dedicated not-found state as the landing page.
  const loadEvent = useCallback(async () => {
    if (eventSlug === undefined) return;
    setTypesLoad('loading');
    setTypesError(undefined);
    try {
      const res = await api.getEvent(slug, eventSlug);
      setTypes([res.bookingType]);
      setSelectedType(res.bookingType);
      setSelectedSlot(null);
      setTypesLoad('ready');
      setStep('slots');
      void loadSlots(res.bookingType, tzRef.current);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setTypesLoad('notfound');
        return;
      }
      setTypesError(messageFor(err, 'We could not load this booking event.'));
      setTypesLoad('error');
    }
  }, [slug, eventSlug, loadSlots]);

  // Run the correct first load (event vs type list). Also the error-retry action.
  const reload = useCallback(() => {
    if (singleEvent) void loadEvent();
    else void loadTypes();
  }, [singleEvent, loadEvent, loadTypes]);

  useEffect(() => {
    reload();
  }, [reload]);

  function chooseType(type: BookingType): void {
    setSelectedType(type);
    setSelectedSlot(null);
    setStep('slots');
    void loadSlots(type, tz);
  }

  function chooseSlot(slot: Slot): void {
    setSelectedSlot(slot);
    setSubmitError(undefined);
    setStep('details');
  }

  async function confirm(customer: { name: string; email: string; note?: string }): Promise<void> {
    if (!selectedType || !selectedSlot) return;
    setSubmitting(true);
    setSubmitError(undefined);
    try {
      const res = await api.createBooking(slug, {
        bookingTypeId: selectedType.id,
        start: selectedSlot.start,
        customer,
      });
      setBooking(res.booking);
      setManageToken(res.manageToken);
      setStep('done');
    } catch (err) {
      // A taken/expired slot means our cached availability is stale: send the
      // visitor back to the picker with a clear message and refreshed slots.
      if (err instanceof ApiError && (err.status === 409 || err.status === 422)) {
        setSubmitError(undefined);
        setSlotsError(
          err.status === 409
            ? 'That time was just taken. Please choose another slot.'
            : 'That time is no longer available. Please choose another slot.',
        );
        setStep('slots');
        setSelectedSlot(null);
        if (selectedType) void loadSlots(selectedType, tz);
      } else {
        setSubmitError(messageFor(err, 'Something went wrong confirming your booking.'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  function changeTz(zone: string): void {
    setTz(zone);
    // Availability is timezone-independent (UTC instants); no refetch needed,
    // the picker just re-renders the same slots in the new zone.
  }

  function backToTypes(): void {
    setStep('types');
    setSelectedType(null);
    setSelectedSlot(null);
    setSlotsError(undefined);
  }

  function backToSlots(): void {
    setStep('slots');
    setSelectedSlot(null);
    setSubmitError(undefined);
  }

  const stepIndex: 0 | 1 | 2 = step === 'types' ? 0 : step === 'slots' ? 1 : 2;

  // Announce each step transition to assistive tech: swapping the entire step
  // view otherwise changes the page silently (focus falls back to <body> and a
  // screen reader gets no signal). A polite live region narrates the new step.
  const stepAnnouncement =
    step === 'types'
      ? 'Step 1 of 3, choose a booking type'
      : step === 'slots'
        ? 'Step 2 of 3, choose a time'
        : step === 'details'
          ? 'Step 3 of 3, enter your details'
          : 'Booking confirmed';

  return (
    <BrandShell>
      <p className="sr-only" role="status" aria-live="polite">
        {stepAnnouncement}
      </p>
      {step !== 'done' ? (
        <header className="page-head">
          <span className="eyebrow">Book a meeting</span>
          {/* Name whose calendar this is, once the host profile has loaded and the
              slug resolves. Until then (or if the profile fetch failed) we keep the
              brand copy. The not-found state never shows a name. */}
          <h1 className="page-title">
            {hostName && typesLoad !== 'notfound'
              ? `Book time with ${hostName}`
              : 'Schedule time with Numaco'}
          </h1>
          <p className="page-desc">
            Pick what you need and a time that suits you. You will get a calendar invite right
            away.
          </p>
        </header>
      ) : null}

      {step !== 'done' ? <Stepper current={stepIndex} /> : null}

      {step === 'types' ? (
        <>
          {typesLoad === 'loading' ? (
            <CardSkeletons count={3} />
          ) : typesLoad === 'notfound' ? (
            <StateBlock
              title="Booking page not found"
              action={
                <button type="button" className="btn btn-ghost" onClick={() => navigate('/')}>
                  Go to the home page
                </button>
              }
            >
              We could not find a booking page at this address. Please check the link and try
              again.
            </StateBlock>
          ) : typesLoad === 'error' ? (
            <StateBlock
              title="Could not load options"
              action={
                <button type="button" className="btn btn-ghost" onClick={() => reload()}>
                  Try again
                </button>
              }
            >
              {typesError}
            </StateBlock>
          ) : types.length === 0 ? (
            <StateBlock title="Nothing to book yet">
              There are no booking options available right now. Please check back later.
            </StateBlock>
          ) : (
            <BookingTypeList types={types} onSelect={chooseType} />
          )}
        </>
      ) : null}

      {step === 'slots' && selectedType ? (
        <>
          {/* Private-event pages address one event directly, so there is no type
              list to return to; the "all booking types" link is hidden there. */}
          {!singleEvent ? (
            <button type="button" className="back-link" onClick={backToTypes}>
              ← All booking types
            </button>
          ) : null}
          {slotsError && slotsLoad === 'ready' ? (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Banner kind="warn">{slotsError}</Banner>
            </div>
          ) : null}
          {/* When the selected type carries a front buffer, flag the delayed start
              up front (bold lead-in) so the customer notes the real start time,
              then frame the buffer as a benefit (no wait if a previous meeting
              overruns). Orange "notice" kind, distinct from the amber warn used
              for load errors. Dash-free copy (house style). */}
          {(selectedType.frontBufferMin ?? 0) > 0 ? (
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <Banner kind="notice">
                <strong>Delayed start, please note the start time</strong>: This meeting has a
                built in start buffer of {selectedType.frontBufferMin} minutes. This is so that a
                previous meeting overrunning never keeps you waiting! The times shown below are
                when the meeting actually begins.
              </Banner>
            </div>
          ) : null}
          <SlotPicker
            bookingType={selectedType}
            slots={slots}
            tz={tz}
            onTzChange={changeTz}
            status={slotsLoad}
            errorMessage={slotsError}
            onSelect={chooseSlot}
            onRetry={() => void loadSlots(selectedType, tz)}
          />
        </>
      ) : null}

      {step === 'details' && selectedType && selectedSlot ? (
        <>
          <button type="button" className="back-link" onClick={backToSlots}>
            ← Change time
          </button>
          <DetailsForm
            bookingType={selectedType}
            slot={selectedSlot}
            tz={tz}
            submitting={submitting}
            submitError={submitError}
            onSubmit={(c) => void confirm(c)}
          />
        </>
      ) : null}

      {step === 'done' && booking ? (
        // Post-confirm: source the shift from the 201 RESPONSE booking's
        // frontBufferMin (the value the server actually persisted, after its
        // defensive clamp), not the live selected type, so the confirmation always
        // matches the created record and the calendar invite it wrote.
        <Confirmation
          booking={booking}
          manageToken={manageToken}
          slug={slug}
          tz={tz}
          typeName={selectedType?.name ?? 'your meeting'}
          frontBufferMin={booking.frontBufferMin ?? 0}
        />
      ) : null}
    </BrandShell>
  );
}

/** Map an unknown error to a user-facing message, preferring ApiError detail. */
function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 0) return 'Network problem. Check your connection and try again.';
    if (err.status === 404) return 'This host is not taking bookings right now.';
    return err.message || fallback;
  }
  return fallback;
}
